import { useEffect, useState } from "react";

interface BreakpointState {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouch: boolean;
  width: number;
}

/**
 * Unified breakpoint detection hook using matchMedia for performance.
 * Breakpoints: mobile < 768px, tablet 768-1023px, desktop >= 1024px
 * Touch detection via (pointer: coarse) media query.
 */
const getInitialState = (): BreakpointState => {
  if (typeof window === "undefined") {
    return { isMobile: false, isTablet: false, isDesktop: true, isTouch: false, width: 1024 };
  }
  const w = window.innerWidth;
  return {
    isMobile: w < 768,
    isTablet: w >= 768 && w < 1024,
    isDesktop: w >= 1024,
    isTouch: window.matchMedia("(pointer: coarse)").matches,
    width: w,
  };
};

export const useBreakpoint = (): BreakpointState => {
  const [state, setState] = useState<BreakpointState>(getInitialState);

  useEffect(() => {
    // SSR safety check
    if (typeof window === "undefined") {
      return;
    }

    // Create media queries for breakpoints
    const mobileQuery = window.matchMedia("(max-width: 767px)");
    const tabletQuery = window.matchMedia("(min-width: 768px) and (max-width: 1023px)");
    const desktopQuery = window.matchMedia("(min-width: 1024px)");
    const touchQuery = window.matchMedia("(pointer: coarse)");

    // Update state based on current media query matches
    const updateState = () => {
      setState({
        isMobile: mobileQuery.matches,
        isTablet: tabletQuery.matches,
        isDesktop: desktopQuery.matches,
        isTouch: touchQuery.matches,
        width: window.innerWidth,
      });
    };

    // Set initial state
    updateState();

    // Listen for changes
    mobileQuery.addEventListener("change", updateState);
    tabletQuery.addEventListener("change", updateState);
    desktopQuery.addEventListener("change", updateState);
    touchQuery.addEventListener("change", updateState);

    // Cleanup
    return () => {
      mobileQuery.removeEventListener("change", updateState);
      tabletQuery.removeEventListener("change", updateState);
      desktopQuery.removeEventListener("change", updateState);
      touchQuery.removeEventListener("change", updateState);
    };
  }, []);

  return state;
};

/**
 * Legacy hook for backward compatibility.
 * Returns only isMobile and width from useBreakpoint.
 * @deprecated Use useBreakpoint() instead for full breakpoint detection.
 */
export const useIsMobile = () => {
  const { isMobile, width } = useBreakpoint();
  return { isMobile, width };
};
